import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Alert,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  VpnKey as KeyIcon,
  ContentCopy as CopyIcon,
  Visibility as ShowIcon,
  VisibilityOff as HideIcon,
  Edit as EditIcon,
  Block as DeactivateIcon,
  CheckCircleOutline as ActivateIcon,
  AutoFixHigh as GenerateIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { EasyCheckButton } from '../../common/Controls';
import { zohoService, ZOHO_WEBHOOK_URL } from '../../../services/zoho/zohoService';

// Webhook keys (Admin): view, auto-generate, add your own, change, deactivate/reactivate.
// Generating a new key never disables the old one — the admin deactivates the old key after updating
// the X-API-Key header in the Zoho workflow, so no payments are rejected during the switch.

const SettingsCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  marginBottom: theme.spacing(3),
  border: `1px solid ${theme.palette.divider}`,
}));

const maskKey = (key) => (key && key.length > 4 ? `${'•'.repeat(Math.min(16, key.length - 4))}${key.slice(-4)}` : '••••');

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
};

const ZohoWebhookKeysCard = ({ planAllowed, showSuccess, showError }) => {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['zohoWebhookKeys'],
    queryFn: () => zohoService.getWebhookKeys(),
    retry: false,
  });

  const [revealed, setRevealed] = useState(() => new Set());
  const [editor, setEditor] = useState(null); // { mode: 'add' | 'edit', id?, value }
  const [confirm, setConfirm] = useState(null); // key being deactivated
  const [busy, setBusy] = useState(false);

  // Lower plans without any webhook key: nothing to manage here
  if (error && !planAllowed) return null;

  const keys = data?.keys || [];
  const activeCount = keys.filter((k) => k.isActive).length;

  const apply = (next) => queryClient.setQueryData(['zohoWebhookKeys'], next);

  const run = async (fn, successMessage) => {
    setBusy(true);
    try {
      apply(await fn());
      if (successMessage) showSuccess(successMessage);
      return true;
    } catch (e) {
      showError(e.message);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const copy = (text, label) => {
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(text).then(() => showSuccess(`${label} copied`));
  };

  const toggleReveal = (id) =>
    setRevealed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const handleGenerate = () =>
    run(
      () => zohoService.createWebhookKey(''),
      'New key generated. Copy it into the X-API-Key header of your Zoho workflow, then deactivate the old key.'
    );

  const handleSaveEditor = async () => {
    const value = editor.value.trim();
    const ok =
      editor.mode === 'add'
        ? await run(() => zohoService.createWebhookKey(value), 'Webhook key added')
        : await run(() => zohoService.updateWebhookKey(editor.id, value), 'Webhook key changed — update it in your Zoho workflow now');
    if (ok) setEditor(null);
  };

  const handleSetActive = async (key, active) => {
    const ok = await run(() => zohoService.setWebhookKeyActive(key.id, active), active ? 'Key activated' : 'Key deactivated');
    if (ok) setConfirm(null);
  };

  return (
    <SettingsCard>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <KeyIcon sx={{ color: '#2563eb', fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
            Webhook Connection
          </Typography>
          {data?.canCreate && (
            <>
              <EasyCheckButton buttonType="form" variant="outlined" onClick={() => setEditor({ mode: 'add', value: '' })} disabled={busy}>
                Add my own key
              </EasyCheckButton>
              <EasyCheckButton buttonType="form" startIcon={<GenerateIcon />} onClick={handleGenerate} loading={busy && !editor && !confirm}>
                Generate new key
              </EasyCheckButton>
            </>
          )}
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Use these details in the Zoho Books workflow rule (Settings &rarr; Automation &rarr; Webhooks) that sends
          vendor payments to EasyCheck.
        </Typography>

        <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, width: '30%' }}>URL to notify</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem', wordBreak: 'break-all' }}>
                  {ZOHO_WEBHOOK_URL}
                  <Tooltip title="Copy URL">
                    <IconButton size="small" onClick={() => copy(ZOHO_WEBHOOK_URL, 'Webhook URL')} sx={{ ml: 0.5 }}>
                      <CopyIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Method</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>POST</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Header name</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{data?.headerName || 'X-API-Key'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Body</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>Raw &middot; $&#123;JSONString&#125; (Vendor Payments)</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : error ? (
          <Alert severity="error">{error.message}</Alert>
        ) : keys.length === 0 ? (
          <Alert severity="info">No webhook key yet. Click "Generate new key" to create one.</Alert>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Key</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {keys.map((k) => (
                  <TableRow key={k.id} sx={{ opacity: k.isActive ? 1 : 0.6 }}>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem', wordBreak: 'break-all' }}>
                      {revealed.has(k.id) ? k.apiKey : maskKey(k.apiKey)}
                      <Tooltip title={revealed.has(k.id) ? 'Hide' : 'Show'}>
                        <IconButton size="small" onClick={() => toggleReveal(k.id)} sx={{ ml: 0.5 }}>
                          {revealed.has(k.id) ? <HideIcon fontSize="inherit" /> : <ShowIcon fontSize="inherit" />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Copy key">
                        <IconButton size="small" onClick={() => copy(k.apiKey, 'Key')}>
                          <CopyIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={k.isActive ? 'Active' : 'Inactive'} color={k.isActive ? 'success' : 'default'} variant="outlined" />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title={k.notes || ''}>
                        <span>{formatDate(k.createdAt)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="Change key">
                        <span>
                          <IconButton size="small" disabled={busy} onClick={() => setEditor({ mode: 'edit', id: k.id, value: k.apiKey })}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      {k.isActive ? (
                        <Tooltip title="Deactivate">
                          <span>
                            <IconButton size="small" color="error" disabled={busy} onClick={() => setConfirm(k)}>
                              <DeactivateIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Activate">
                          <span>
                            <IconButton size="small" color="success" disabled={busy} onClick={() => handleSetActive(k, true)}>
                              <ActivateIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {activeCount > 1 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {activeCount} keys are active. Once your Zoho workflow uses the new key, deactivate the old one.
          </Alert>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          To change your key safely: Generate new key &rarr; paste it into the X-API-Key header in Zoho &rarr; deactivate the old
          key here. Keep keys confidential — anyone with an active key can send payments to your EasyCheck account.
        </Typography>
      </CardContent>

      {/* Add / change key */}
      <Dialog open={Boolean(editor)} onClose={() => !busy && setEditor(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{editor?.mode === 'add' ? 'Add your own webhook key' : 'Change webhook key'}</DialogTitle>
        <DialogContent>
          {editor?.mode === 'edit' && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Zoho payments sent with the old value will be rejected until you update the X-API-Key header in your Zoho
              workflow. To avoid any gap, use "Generate new key" instead — it keeps this key working until you deactivate it.
            </Alert>
          )}
          <TextField
            fullWidth
            size="small"
            label="Key"
            value={editor?.value || ''}
            onChange={(e) => setEditor((ed) => ({ ...ed, value: e.target.value }))}
            helperText="24-100 characters: letters, numbers, '-', '_' and '.'. Leave blank to auto-generate a secure key."
            InputProps={{ sx: { fontFamily: 'monospace' } }}
            autoComplete="off"
          />
        </DialogContent>
        <DialogActions>
          <EasyCheckButton buttonType="form" variant="outlined" onClick={() => setEditor(null)} disabled={busy}>
            Cancel
          </EasyCheckButton>
          <EasyCheckButton buttonType="form" onClick={handleSaveEditor} loading={busy}>
            {editor?.value?.trim() ? 'Save' : 'Auto-generate'}
          </EasyCheckButton>
        </DialogActions>
      </Dialog>

      {/* Deactivate confirmation */}
      <Dialog open={Boolean(confirm)} onClose={() => !busy && setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Deactivate this key?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Zoho payments sent with key <b>{confirm ? maskKey(confirm.apiKey) : ''}</b> will be rejected.
            {activeCount <= 1 && ' This is your only active key, so the webhook will stop creating cheques until you activate or generate a key.'}{' '}
            You can activate it again at any time.
          </Typography>
        </DialogContent>
        <DialogActions>
          <EasyCheckButton buttonType="form" variant="outlined" onClick={() => setConfirm(null)} disabled={busy}>
            Cancel
          </EasyCheckButton>
          <EasyCheckButton buttonType="form" color="error" onClick={() => handleSetActive(confirm, false)} loading={busy}>
            Deactivate
          </EasyCheckButton>
        </DialogActions>
      </Dialog>
    </SettingsCard>
  );
};

export default ZohoWebhookKeysCard;
